import { rem } from 'polished'
import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import * as Analytics from 'analytics/analytics'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import WalledContent from 'components/Common/WalledContent'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { pluralizeToString } from 'lib/string/pluralize'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import {
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import {
  getPropertyCheapestRates,
  arePropertyRatesLoading,
} from 'selectors/offerPage/offerPageSelectors'

const SummaryPane = styled(Pane)`
  position: relative;

  > * + * {
    margin-top: ${rem(12)};
  }
`

interface Props {
  lpcOffer: App.HotelOffer;
  duration: number;
  isLoading: boolean;
  cheapestPropertyRates: [App.PropertyRate | undefined, App.PropertyRate | undefined];
  searchId: string | undefined;
  /** TODO for styled media queries */
  className?: string;
  onViewPackageOptions: () => void;
}

function PropertySummary({
  lpcOffer,
  duration,
  isLoading,
  cheapestPropertyRates,
  searchId,
  className,
  onViewPackageOptions,
}: Props) {
  const [hotelOnlyRate, hotelWithFlightsRate] = cheapestPropertyRates

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!hotelOnlyRate) return [undefined]
    return [
      generateLoyaltyPointsCalculatorPropertyOptions(hotelOnlyRate),
    ]
  }, [hotelOnlyRate])

  const memberDiscount = hotelOnlyRate?.luxPlusTotal?.discount ?? 0
  const nonMemberDiscount = hotelOnlyRate?.total.discount ?? 0
  const discount = memberDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? memberDiscount : nonMemberDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? nonMemberDiscount : undefined

  const hasTrackedPrice = useRef(false)
  const hotelOnlyDisplayPrice = hotelOnlyRate?.total.displayPrice
  useEffect(() => {
    if (searchId && !isLoading && !hasTrackedPrice.current) {
      if (hotelOnlyDisplayPrice) {
        Analytics.trackClientEvent({
          subject: `searchId:${searchId}_offerId:${lpcOffer.id}`,
          action: hotelOnlyDisplayPrice.toString(),
          category: 'price_accuracy',
          type: 'operational',
        })
      }
      // If we're not loading and there's no cheapest price, it's likely that we don't have dates
      // set (i.e. we arrived via an anytime search result). Since we're not displaying initial
      // rates, we must refrain from firing the pricing accuracy event when a cheapest rate _does_
      // become available.
      //
      // For the case where dates _are_ set, we're relying on clean transitions (i.e. no gaps due to
      // races) from `isLoading` being set to `hotelOnlyRate` being available.
      hasTrackedPrice.current = true
    }
  }, [searchId, isLoading, hotelOnlyDisplayPrice, lpcOffer])

  return <SummaryPane border="neutral-six" borderRadius="S" padding="28 20" shadow="bottom-small" className={className}>
    <WalledContent
      enforced={lpcOffer.walledGarden}
      wall={<BodyText variant="large" weight="bold">{lpcOffer.durationLabel} packages available</BodyText>}
    >
      <Group direction="vertical" horizontalAlign="center" gap={8}>
        <Group direction="vertical" horizontalAlign="center" gap={4}>
          <Group direction="vertical" horizontalAlign="center">
            <LoadingIndicator floating visible={isLoading} opaque backdrop="white" />
            {/* TODO offerConfig from `lpcOffer.luxPlus`? */}
            {hotelOnlyRate && <>
              <PriceRowPriceCaption>{pluralizeToString('night', duration)} from</PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="L"
                price={hotelOnlyRate.total.displayPrice}
                memberPrice={hotelOnlyRate.luxPlusTotal?.displayPrice}
                saleUnit="total"
                testid="hotel-summary-price"
                horizontalAlign="center"
              />
              <PriceRowTaxesAndFees />
              {/* TODO price breakdown for above? */}
              {/* TODO extra guest surcharges? */}
              <PriceRowValueDiscountWithCaption
                size="M"
                originalValue={hotelOnlyRate.luxPlusTotal?.valuedUpToPrice ?? hotelOnlyRate.total.valuedUpToPrice}
                discountPercentage={discount}
              />
            </>}
          </Group>
          {hotelOnlyRate && <LoyaltyPointsEarn
            horizontalAlign="center"
            offer={lpcOffer}
            calculationType="estimate"
            calculationRequests={pointsEarnCalculationRequests}
          />}
          {hotelWithFlightsRate?.includesFlights && <Group direction="vertical" horizontalAlign="center">
            {typeof hotelWithFlightsRate.cheapestFlightPrice === 'number' && hotelWithFlightsRate.cheapestFlightPrice > 0 && <PriceRowFlightPackage
              product="hotel"
              price={hotelWithFlightsRate.luxPlusTotal?.displayPrice ?? hotelWithFlightsRate.total.displayPrice}
            />}
          </Group>}
        </Group>
      </Group>
    </WalledContent>
    <TextButton onClick={onViewPackageOptions} kind="primary" fit="flex">
      View package options
    </TextButton>
  </SummaryPane>
}

export default withOfferPageAndReduxState((state, appState) => ({
  duration: state.duration,
  isLoading: arePropertyRatesLoading(state, appState),
  cheapestPropertyRates: getPropertyCheapestRates(state, appState),
  searchId: appState.router.location.query.searchId,
  open: !!state.activeFilter,
}), PropertySummary, preventRenderWithOpenFilter)
